// Reusable confirmation modal — shared across web and admin
// Uses DaisyUI modal component and the common i18n namespace

import { useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDialogFocus } from '../hooks/useDialogFocus';
import { useScrollLock } from '../hooks/useScrollLock';

interface ConfirmModalProps {
  isOpen: boolean;
  title?: string;
  message: string;
  confirmLabel?: string;
  confirmClassName?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'info';
  isLoading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmModal({
  isOpen,
  title,
  message,
  confirmLabel,
  confirmClassName,
  cancelLabel,
  variant = 'danger',
  isLoading = false,
  onConfirm,
  onCancel,
}: ConfirmModalProps) {
  const { t } = useTranslation('common');

  const dialogRef = useRef<HTMLDialogElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const messageId = useId();

  // Must run every render (hooks can't be conditional) — the early return below is after it.
  useScrollLock(isOpen);
  // Initial focus on Cancel (the least destructive action); Escape cancels unless a
  // confirm is in flight, matching the disabled buttons.
  useDialogFocus({
    isOpen,
    containerRef: dialogRef,
    initialFocusRef: cancelRef,
    onEscape: () => {
      if (!isLoading) onCancel();
    },
  });

  // Variant-specific colors and icons
  const variantConfig = {
    danger: { btn: 'btn-error', bar: 'bg-error', iconColor: 'text-error' },
    warning: { btn: 'btn-warning', bar: 'bg-warning', iconColor: 'text-warning' },
    info: { btn: 'btn-info', bar: 'bg-info', iconColor: 'text-info' },
  };

  const config = variantConfig[variant];

  if (!isOpen) return null;

  return (
    // data-theme="dark" — shared across contexts that may not have a dark ancestor, so it
    // must not depend on theme inheritance.
    // `open` keeps it in the accessibility tree (DaisyUI's `modal-open` only shows it visually);
    // `aria-modal` because it's rendered without `showModal()`.
    <dialog
      ref={dialogRef}
      open
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
      aria-describedby={messageId}
      className="modal modal-open text-[#CACACA]"
    >
      <div className="modal-box p-0 overflow-hidden flex bg-[#1E1E1E]">
        {/* Colored left bar with icon */}
        <div className={`${config.bar} w-12 shrink-0 flex items-start justify-center pt-6`}>
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>

        {/* Content */}
        <div className="flex-1 p-6">
          {/* Close button */}
          <button
            type="button"
            className="btn btn-sm btn-ghost absolute right-2 top-2"
            onClick={onCancel}
            aria-label={t('common.close')}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          {title && (
            <p id={titleId} className="py-2 text-sm">
              {title}
            </p>
          )}
          <p id={messageId} className="py-2 text-sm">
            {message}
          </p>

          <div className="flex justify-end gap-2 pt-4">
            <button
              ref={cancelRef}
              className="btn btn-outline btn-sm"
              onClick={onCancel}
              disabled={isLoading}
            >
              {cancelLabel || t('common.cancel')}
            </button>
            <button
              className={`btn btn-sm ${confirmClassName ?? config.btn}`}
              onClick={onConfirm}
              disabled={isLoading}
            >
              {isLoading && <span className="loading loading-spinner loading-xs" />}
              {confirmLabel || t('common.confirm')}
            </button>
          </div>
        </div>
      </div>
      <div className="modal-backdrop bg-black/50" onClick={onCancel} />
    </dialog>
  );
}
